class ColumnSlider {
    constructor(sliderSection, options = {}) {
        // Configuration
        this.columnWidth = options.columnWidth;
        this.gap = options.gap;
        this.infinite = options.infinite || true; // New infinite option
        this.autoplay = options.autoplay || false; // Optional autoplay
        this.autoplayDelay = options.autoplayDelay || 3000; // Autoplay delay in ms
        this.transitionDuration = options.transitionDuration || 300; // Transition duration in ms
        this.clickToAdvance = options.clickToAdvance !== false; // Default true for click to advance

        // Elements
        this.sliderSection = sliderSection;
        this.columnsWrapper = sliderSection.querySelector('.columns-wrapper');
        this.columnsContainer = sliderSection.querySelector('.columns');
        this.navControls = sliderSection.querySelector('.nav-controls');
        this.prevArrow = sliderSection.querySelector('.nav-arrow.prev');
        this.nextArrow = sliderSection.querySelector('.nav-arrow.next');

        // State
        this.currentSlide = 0;
        this.maxSlide = 0;
        this.isTransitioning = false;
        this.autoplayTimer = null;
        this.clonedElements = [];
        this.columnClickHandlers = new Map(); // Store click handlers for cleanup
        this.updateTotalSlides();

        // Touch support
        this.touchStartX = 0;
        this.touchEndX = 0;

        // Initialize
        this.init();
    }

    updateTotalSlides() {
        // Look for any element with 'column' class (handles multiple classes)
        const columns = this.columnsContainer.querySelectorAll('.column:not(.clone)');
        this.totalSlides = columns.length;
        this.originalColumns = Array.from(columns);
    }

    init() {
        // Set up infinite slider if enabled
        if (this.infinite && this.totalSlides > 0) {
            this.setupInfiniteSlider();
        }

        // Set up event listeners
        this.prevArrow.addEventListener('click', () => this.slide('prev'));
        this.nextArrow.addEventListener('click', () => this.slide('next'));

        // Set active slide
        const columns = this.columnsContainer.querySelectorAll('.column:not(.clone)');
        if (columns[this.currentSlide]) {
            columns[this.currentSlide].classList.add('active');
        }

        // Set up click handlers for columns
        if (this.clickToAdvance) {
            this.setupColumnClickHandlers();
        }

        // Touch/Swipe support
        this.columnsContainer.addEventListener('touchstart', (e) => this.handleTouchStart(e));
        this.columnsContainer.addEventListener('touchend', (e) => this.handleTouchEnd(e));

        // Transition end listener for infinite slider
        if (this.infinite) {
            this.columnsContainer.addEventListener('transitionend', () => this.handleTransitionEnd());
        }

        // Resize listener
        this.resizeHandler = () => this.handleResize();
        window.addEventListener('resize', this.resizeHandler);

        // Autoplay
        if (this.autoplay && this.totalSlides > 1) {
            this.startAutoplay();
            
            // Pause on hover
            this.sliderSection.addEventListener('mouseenter', () => this.stopAutoplay());
            this.sliderSection.addEventListener('mouseleave', () => this.startAutoplay());
        }

        // Initial setup - add small delay to ensure proper measurements
        setTimeout(() => {
            this.checkNavigation();
            this.updateSlider();
        }, 10);
    }

    setupColumnClickHandlers() {
        // Remove existing handlers
        this.removeColumnClickHandlers();

        // Add click handlers to all columns (including clones)
        const allColumns = this.columnsContainer.querySelectorAll('.column');
        allColumns.forEach(column => {
            const clickHandler = (e) => this.handleColumnClick(e);
            column.addEventListener('click', clickHandler);
            this.columnClickHandlers.set(column, clickHandler);

            // Add cursor pointer to indicate clickability
            column.style.cursor = 'pointer';
        });
    }

    removeColumnClickHandlers() {
        this.columnClickHandlers.forEach((handler, column) => {
            column.removeEventListener('click', handler);
            column.style.cursor = '';
        });
        this.columnClickHandlers.clear();
    }

    handleColumnClick(e) {
        // Prevent triggering if clicking on interactive elements within the column
        const clickedElement = e.target;
        const interactiveElements = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
        
        // Check if clicked element or any parent is an interactive element
        let element = clickedElement;
        while (element && element !== e.currentTarget) {
            if (interactiveElements.includes(element.tagName) || 
                element.hasAttribute('data-no-slide')) {
                return; // Don't advance slide if clicking on interactive element
            }
            element = element.parentElement;
        }

        // Advance to next slide
        this.slide('next');
    }

    setupInfiniteSlider() {
        // Clear any existing clones
        this.clearClones();

        const wrapperWidth = this.columnsWrapper.offsetWidth;
        const firstColumn = this.columnsContainer.querySelector('.column');
        
        if (!firstColumn) return;

        const actualColumnWidth = firstColumn.getBoundingClientRect().width;
        const containerStyle = window.getComputedStyle(this.columnsContainer);
        const actualGap = parseFloat(containerStyle.gap) || this.gap;
        
        // Calculate how many columns fit in view
        const columnsInView = Math.ceil((wrapperWidth + actualGap) / (actualColumnWidth + actualGap));
        
        // Clone columns for seamless infinite scroll
        const clonesToAdd = Math.min(columnsInView + 2, this.totalSlides);
        
        // Add clones at the beginning (from the end of original columns)
        for (let i = 0; i < clonesToAdd; i++) {
            const indexToClone = this.totalSlides - clonesToAdd + i;
            const clone = this.originalColumns[indexToClone].cloneNode(true);
            clone.classList.add('clone');
            clone.classList.remove('active');
            this.columnsContainer.insertBefore(clone, this.columnsContainer.firstChild);
            this.clonedElements.push(clone);
        }
        
        // Add clones at the end (from the beginning of original columns)
        for (let i = 0; i < clonesToAdd; i++) {
            const clone = this.originalColumns[i].cloneNode(true);
            clone.classList.add('clone');
            clone.classList.remove('active');
            this.columnsContainer.appendChild(clone);
            this.clonedElements.push(clone);
        }
        
        // Adjust initial position to show the first real slide (not clone)
        this.infiniteOffset = clonesToAdd;
        this.currentSlide = 0;
        this.visualSlide = 0; // Track visual position separately
        
        // Set initial position without animation
        this.columnsContainer.style.transition = 'none';
        const initialOffset = this.infiniteOffset * (actualColumnWidth + actualGap);
        this.columnsContainer.style.transform = `translateX(-${initialOffset}px)`;
        
        // Force reflow before re-enabling transitions
        this.columnsContainer.offsetHeight;
        
        // Re-enable transitions
        this.columnsContainer.style.transition = `transform ${this.transitionDuration}ms ease`;

        // Re-setup click handlers for clones if click to advance is enabled
        if (this.clickToAdvance) {
            this.setupColumnClickHandlers();
        }
    }

    clearClones() {
        this.clonedElements.forEach(clone => {
            if (clone.parentNode) {
                clone.parentNode.removeChild(clone);
            }
        });
        this.clonedElements = [];
    }

    checkNavigation() {
        // Handle empty sliders
        if (this.totalSlides === 0) {
            this.navControls.classList.remove('visible');
            return false;
        }

        // Always show navigation for infinite sliders with more than 1 slide
        if (this.infinite && this.totalSlides > 1) {
            this.navControls.classList.add('visible');
            this.updateArrows();
            return true;
        }

        const wrapperWidth = this.columnsWrapper.offsetWidth;

        // Get the actual column width from the first column element
        const firstColumn = this.columnsContainer.querySelector('.column:not(.clone)');

        if (!firstColumn) {
            console.warn('No columns found in slider');
            this.navControls.classList.remove('visible');
            return false;
        }

        // Use computed style to get actual width including any CSS modifications
        const computedStyle = window.getComputedStyle(firstColumn);
        const actualColumnWidth = firstColumn.getBoundingClientRect().width;

        // Calculate gap from computed styles if possible
        const containerStyle = window.getComputedStyle(this.columnsContainer);
        const actualGap = parseFloat(containerStyle.gap) || this.gap;

        const totalContentWidth = (actualColumnWidth * this.totalSlides) +
                                 (actualGap * (this.totalSlides - 1));

        if (totalContentWidth > wrapperWidth) {
            // Calculate how many columns fit in view
            const columnsInView = Math.floor((wrapperWidth + actualGap) /
                                             (actualColumnWidth + actualGap));
            this.maxSlide = Math.max(0, this.totalSlides - columnsInView);

            // Show navigation if needed
            this.navControls.classList.add('visible');
            this.updateArrows();
            return true;
        } else {
            // Hide navigation if all columns fit
            this.navControls.classList.remove('visible');
            this.currentSlide = 0;
            this.columnsContainer.style.transform = 'translateX(0)';
            return false;
        }
    }

    updateArrows() {
        if (this.infinite) {
            // For infinite slider, arrows are never disabled
            this.prevArrow.classList.remove('disabled');
            this.nextArrow.classList.remove('disabled');
        } else {
            // Standard behavior for non-infinite slider
            if (this.currentSlide === 0) {
                this.prevArrow.classList.add('disabled');
            } else {
                this.prevArrow.classList.remove('disabled');
            }

            if (this.currentSlide >= this.maxSlide) {
                this.nextArrow.classList.add('disabled');
            } else {
                this.nextArrow.classList.remove('disabled');
            }
        }
    }

    slide(direction) {
        if (this.isTransitioning && this.infinite) return;

        // Remove active class from current slide
        const columns = this.columnsContainer.querySelectorAll('.column:not(.clone)');
        if (columns[this.currentSlide]) {
            columns[this.currentSlide].classList.remove('active');
        }

        if (this.infinite) {
            this.isTransitioning = true;
            
            // For infinite slider, we track both logical and visual positions
            if (direction === 'prev') {
                this.visualSlide = (this.visualSlide !== undefined ? this.visualSlide : this.currentSlide) - 1;
                this.currentSlide--;
                if (this.currentSlide < 0) {
                    this.currentSlide = this.totalSlides - 1;
                }
            } else if (direction === 'next') {
                this.visualSlide = (this.visualSlide !== undefined ? this.visualSlide : this.currentSlide) + 1;
                this.currentSlide++;
                if (this.currentSlide >= this.totalSlides) {
                    this.currentSlide = 0;
                }
            }
        } else {
            // Standard non-infinite behavior
            const needsNav = this.checkNavigation();
            if (!needsNav) return;

            if (direction === 'prev' && this.currentSlide > 0) {
                this.currentSlide--;
            } else if (direction === 'next' && this.currentSlide < this.maxSlide) {
                this.currentSlide++;
            }
        }

        // Add active class to new slide
        if (columns[this.currentSlide]) {
            columns[this.currentSlide].classList.add('active');
        }

        this.updateSlider();
        
        // Reset autoplay timer
        if (this.autoplay) {
            this.stopAutoplay();
            this.startAutoplay();
        }
    }

    updateSlider() {
        if (this.totalSlides === 0) return;

        const firstColumn = this.columnsContainer.querySelector('.column:not(.clone)');
        if (!firstColumn) return;

        const actualColumnWidth = firstColumn.getBoundingClientRect().width;
        const containerStyle = window.getComputedStyle(this.columnsContainer);
        const actualGap = parseFloat(containerStyle.gap) || this.gap;

        let slideDistance;
        
        if (this.infinite) {
            // Use visual slide for smooth infinite scrolling
            const visualPosition = (this.visualSlide !== undefined ? this.visualSlide : this.currentSlide) + this.infiniteOffset;
            slideDistance = visualPosition * (actualColumnWidth + actualGap);
        } else {
            slideDistance = this.currentSlide * (actualColumnWidth + actualGap);
        }
        
        this.columnsContainer.style.transform = `translateX(-${slideDistance}px)`;
        this.updateArrows();
    }

    handleTransitionEnd() {
        if (!this.infinite) return;

        const firstColumn = this.columnsContainer.querySelector('.column:not(.clone)');
        if (!firstColumn) return;

        const actualColumnWidth = firstColumn.getBoundingClientRect().width;
        const containerStyle = window.getComputedStyle(this.columnsContainer);
        const actualGap = parseFloat(containerStyle.gap) || this.gap;

        // Check if we need to reposition after reaching clones
        if (this.visualSlide < 0) {
            // Reached beginning clones, jump to end
            this.columnsContainer.style.transition = 'none';
            this.visualSlide = this.totalSlides - 1;
            const jumpPosition = (this.visualSlide + this.infiniteOffset) * (actualColumnWidth + actualGap);
            this.columnsContainer.style.transform = `translateX(-${jumpPosition}px)`;
            
            // Force reflow
            this.columnsContainer.offsetHeight;
            
            this.columnsContainer.style.transition = `transform ${this.transitionDuration}ms ease`;
        } else if (this.visualSlide >= this.totalSlides) {
            // Reached end clones, jump to beginning
            this.columnsContainer.style.transition = 'none';
            this.visualSlide = 0;
            const jumpPosition = (this.visualSlide + this.infiniteOffset) * (actualColumnWidth + actualGap);
            this.columnsContainer.style.transform = `translateX(-${jumpPosition}px)`;
            
            // Force reflow
            this.columnsContainer.offsetHeight;
            
            this.columnsContainer.style.transition = `transform ${this.transitionDuration}ms ease`;
        }
        
        this.isTransitioning = false;
    }

    handleTouchStart(e) {
        this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
    }

    handleSwipe() {
        const swipeThreshold = 50;
        const diff = this.touchStartX - this.touchEndX;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0) {
                // Swiped left
                this.slide('next');
            } else {
                // Swiped right
                this.slide('prev');
            }
        }
    }

    startAutoplay() {
        if (!this.autoplay || this.totalSlides <= 1) return;
        
        this.stopAutoplay();
        this.autoplayTimer = setInterval(() => {
            this.slide('next');
        }, this.autoplayDelay);
    }

    stopAutoplay() {
        if (this.autoplayTimer) {
            clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }
    }

    handleResize() {
        // Update total slides in case DOM has changed
        this.updateTotalSlides();

        // Rebuild infinite slider if needed
        if (this.infinite && this.totalSlides > 0) {
            // Preserve current slide position
            const savedSlide = this.currentSlide;
            this.setupInfiniteSlider();
            this.currentSlide = savedSlide;
            this.visualSlide = savedSlide;
        }

        this.checkNavigation();
        
        // Ensure current slide is still valid
        if (!this.infinite && this.currentSlide > this.maxSlide) {
            this.currentSlide = this.maxSlide;
        }
        
        this.updateSlider();
    }

    // Public method to refresh the slider if content changes
    refresh() {
        this.updateTotalSlides();
        
        if (this.infinite && this.totalSlides > 0) {
            this.setupInfiniteSlider();
        }
        
        this.currentSlide = 0;
        this.visualSlide = 0;
        this.checkNavigation();
        this.updateSlider();
    }

    // Cleanup method if needed
    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        this.prevArrow.removeEventListener('click', () => this.slide('prev'));
        this.nextArrow.removeEventListener('click', () => this.slide('next'));
        this.columnsContainer.removeEventListener('transitionend', () => this.handleTransitionEnd());
        this.stopAutoplay();
        this.removeColumnClickHandlers();
        this.clearClones();
    }
}

// Initialize all sliders on the page
document.addEventListener('DOMContentLoaded', () => {
    const sliderSections = document.querySelectorAll('.slider-section');
    const sliders = [];

    sliderSections.forEach((section, index) => {
        // You can pass different options for each slider
        const childColumns = section.querySelectorAll('.column');
        if (childColumns.length > 0) {
            const computedStyle = window.getComputedStyle(childColumns[0]);
            var margins = parseInt(computedStyle.marginRight, 10) + parseInt(computedStyle.marginLeft, 10);
            
            // Check for data attributes for configuration
            const isInfinite = section.dataset.infinite === 'true';
            const isAutoplay = section.dataset.autoplay === 'true';
            const autoplayDelay = parseInt(section.dataset.autoplayDelay) || 3000;
            const clickToAdvance = section.dataset.clickToAdvance !== 'false'; // Default true
            
            const slider = new ColumnSlider(section, {
                columnWidth: childColumns[0].offsetWidth,
                gap: margins,
                infinite: isInfinite,
                autoplay: isAutoplay,
                autoplayDelay: autoplayDelay,
                transitionDuration: 300,
                clickToAdvance: clickToAdvance
            });
            sliders.push(slider);
        }
    });

    // Store sliders globally if you need to access them later
    window.sliders = sliders;
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
    // DOMContentLoaded event will fire
} else {
    // DOM is already loaded, initialize now
    const sliderSections = document.querySelectorAll('.slider-section');
    const sliders = [];

    sliderSections.forEach((section, index) => {
        const childColumns = section.querySelectorAll('.column');
        if (childColumns.length > 0) {
            const computedStyle = window.getComputedStyle(childColumns[0]);
            var margins = parseInt(computedStyle.marginRight, 10) + parseInt(computedStyle.marginLeft, 10);
            
            // Check for data attributes for configuration
            const isInfinite = section.dataset.infinite === 'true';
            const isAutoplay = section.dataset.autoplay === 'true';
            const autoplayDelay = parseInt(section.dataset.autoplayDelay) || 3000;
            const clickToAdvance = section.dataset.clickToAdvance !== 'false'; // Default true
            
            const slider = new ColumnSlider(section, {
                columnWidth: childColumns[0].offsetWidth,
                gap: margins,
                infinite: isInfinite,
                autoplay: isAutoplay,
                autoplayDelay: autoplayDelay,
                transitionDuration: 300,
                clickToAdvance: clickToAdvance
            });
            sliders.push(slider);
        }
    });

    window.sliders = sliders;
}